import { gql, useQuery } from '@apollo/client'
import AssetLogo from 'components/Logo/AssetLogo'
import { SupportedChainId } from 'constants/chains'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { formatTokenQuantity } from 'utils/formatNumbers'

import { analyticsApolloClient, v2AnalyticsApolloClient } from '../../graphql/thegraph/apollo'
import { getV3SwapUsdValue } from './swapValue'

const V3_POOL_QUERY = gql`
  query V3PoolDetail($id: ID!, $start: BigInt!) {
    pool(id: $id) {
      id
      feeTier
      tick
      token0Price
      token1Price
      volumeUSD
      untrackedVolumeUSD
      feesUSD
      txCount
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
      token0 {
        id
        symbol
        name
        decimals
        derivedETH
      }
      token1 {
        id
        symbol
        name
        decimals
        derivedETH
      }
      poolDayData(first: 300, orderBy: date, orderDirection: desc) {
        id
        date
        volumeUSD
        tvlUSD
        token0Price
        token1Price
      }
      poolHourData(first: 24, orderBy: periodStartUnix, orderDirection: desc) {
        id
        periodStartUnix
        volumeUSD
        tvlUSD
        token0Price
        token1Price
      }
      ticks(first: 32, orderBy: tickIdx, orderDirection: asc) {
        id
        tickIdx
        liquidityGross
      }
      swaps(first: 1000, orderBy: timestamp, orderDirection: desc, where: { timestamp_gte: $start }) {
        id
        timestamp
        origin
        amount0
        amount1
        amountUSD
        transaction {
          id
        }
      }
    }
    bundles(first: 1) {
      ethPriceUSD
    }
  }
`

const V2_PAIR_QUERY = gql`
  query V2PairDetail($id: ID!) {
    pair(id: $id) {
      id
      token0Price
      token1Price
      volumeUSD
      reserveUSD
      reserve0
      reserve1
      txCount
      token0 {
        id
        symbol
        name
      }
      token1 {
        id
        symbol
        name
      }
      swaps(first: 20, orderBy: timestamp, orderDirection: desc) {
        id
        timestamp
        from
        amount0In
        amount0Out
        amount1In
        amount1Out
        amountUSD
        transaction {
          id
        }
      }
      pairHourData(first: 24, orderBy: hourStartUnix, orderDirection: desc) {
        id
        hourStartUnix
        hourlyVolumeUSD
        reserveUSD
        reserve0
        reserve1
      }
    }
    pairDayDatas(first: 300, orderBy: date, orderDirection: desc, where: { pairAddress: $id }) {
      id
      date
      dailyVolumeUSD
      reserveUSD
      reserve0
      reserve1
    }
  }
`

type Token = { id: string; symbol: string | null; name: string | null; decimals?: string; derivedETH?: string }
type ChartRange = '1d' | '1w' | '1y' | 'all'
const CHART_RANGE_DAYS: Record<Exclude<ChartRange, 'all'>, number> = { '1d': 1, '1w': 7, '1y': 365 }
const CHART_RANGE_LABELS: Record<ChartRange, string> = {
  '1d': '1 day',
  '1w': '1 week',
  '1y': '1 year',
  all: 'All-time',
}
type Day = {
  id: string
  date: number
  volumeUSD: string
  untrackedVolumeUSD: string
  tvlUSD: string
  token0Price: string
  token1Price: string
}
type V3Hour = {
  id: string
  periodStartUnix: number
  volumeUSD: string
  tvlUSD: string
  token0Price: string
  token1Price: string
}
type V3Pool = {
  id: string
  feeTier: string
  tick: string | null
  token0Price: string
  token1Price: string
  volumeUSD: string
  untrackedVolumeUSD: string
  feesUSD: string
  txCount: string
  totalValueLockedUSD: string
  totalValueLockedToken0: string
  totalValueLockedToken1: string
  token0: Token
  token1: Token
  poolDayData: Day[]
  poolHourData: V3Hour[]
  ticks: { id: string; tickIdx: string; liquidityGross: string }[]
  swaps: {
    id: string
    timestamp: string
    origin: string
    amount0: string
    amount1: string
    amountUSD: string
    transaction: { id: string }
  }[]
}
type V2Swap = {
  id: string
  timestamp: string
  from: string
  amount0In: string
  amount0Out: string
  amount1In: string
  amount1Out: string
  amountUSD: string
  transaction: { id: string }
}
type V2Pair = {
  id: string
  token0Price: string
  token1Price: string
  volumeUSD: string
  reserveUSD: string
  reserve0: string
  reserve1: string
  txCount: string
  token0: Token
  token1: Token
  swaps: V2Swap[]
  pairHourData: V2Hour[]
}
type V2Day = {
  id: string
  date: number
  dailyVolumeUSD: string
  reserveUSD: string
  reserve0: string
  reserve1: string
}
type V2Hour = {
  id: string
  hourStartUnix: number
  hourlyVolumeUSD: string
  reserveUSD: string
  reserve0: string
  reserve1: string
}
type Snapshot = {
  id: string
  date: number
  volumeUSD: number
  liquidityUSD: number
  token0Price: number
  token1Price: number
}

const Page = styled.main`
  width: 100%;
  max-width: 1120px;
  padding: 68px 16px 48px;
  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding: 24px 12px 32px;
  }
`
const Back = styled(Link)`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.accentAction};
  }
`
const Header = styled.header`
  margin: 16px 0 28px;
`
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
const TokenIcons = styled.div`
  display: flex;
`
const TokenIcon = styled(AssetLogo)<{ $offset?: boolean }>`
  margin-left: ${({ $offset }) => ($offset ? '-10px' : 0)};
  border: 2px solid ${({ theme }) => theme.backgroundSurface};
  border-radius: 50%;
`
const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 30px;
`
const Subtle = styled.div`
  margin-top: 8px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  overflow-wrap: anywhere;
`
const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
  overflow-x: auto;
`
const Tab = styled.button<{ $active: boolean }>`
  padding: 9px 13px;
  color: ${({ $active, theme }) => ($active ? theme.accentAction : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.accentActionSoft : theme.backgroundSurface)};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease, border-color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
    border-color: ${({ theme }) => theme.accentAction};
    filter: brightness(1.1);
  }
`
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    grid-template-columns: 1fr;
  }
`
const Card = styled.section`
  padding: 20px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`
const ChartTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`
const CornerReadout = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  text-align: right;
  white-space: nowrap;

  strong {
    color: ${({ theme }) => theme.textPrimary};
    font-size: 15px;
  }
`
const RangeControls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  width: 75%;
  margin: 12px auto 0;
  padding: 3px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 999px;
`
const RangeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 38px;
  padding: 6px 9px;
  color: ${({ $active, theme }) => ($active ? theme.textPrimary : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.backgroundModule : 'transparent')};
  border: 0;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
    filter: brightness(1.1);
  }
`
const ChartTitle = styled.h2`
  margin: 0 0 6px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 21px;
`
const ChartValue = styled.div`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 27px;
  font-weight: 600;
`
const ChartCaption = styled.div`
  margin: 6px 0 20px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
`
const Chart = styled.div<{ $count: number }>`
  display: flex;
  align-items: flex-end;
  gap: ${({ $count }) => ($count > 180 ? '0' : $count > 60 ? '1px' : '5px')};
  height: 240px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
  min-width: 0;
  overflow: hidden;
`
const LineChart = styled.svg`
  width: 100%;
  height: 240px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};

  polyline {
    fill: none;
    stroke: #d86624;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 3;
  }
`
const DepthChart = styled.svg`
  width: 100%;
  height: 240px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};

  .depth-bid {
    fill: #f6a04f;
    fill-opacity: 0.28;
    stroke: #d86624;
    stroke-width: 2;
  }
  .depth-ask {
    fill: #f6a04f;
    fill-opacity: 0.28;
    stroke: #d86624;
    stroke-width: 2;
  }
  .depth-mid {
    stroke: ${({ theme }) => theme.backgroundOutline};
    stroke-dasharray: 3 3;
    stroke-width: 1;
  }
`
const PriceChart = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px;
  min-height: 240px;
`
const PriceChartCanvas = styled.div`
  position: relative;
  min-width: 0;
`
const PriceAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 0 22px 12px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  text-align: right;
`
const HoverLine = styled.div<{ $left: number }>`
  position: absolute;
  top: 8px;
  bottom: 22px;
  left: ${({ $left }) => `${$left}%`};
  width: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`
const HoverHorizontal = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  right: 0;
  left: 0;
  height: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`
const HoverPoint = styled.div<{ $left: number; $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  left: ${({ $left }) => `${$left}%`};
  width: 10px;
  height: 10px;
  background: #d86624;
  border: 2px solid ${({ theme }) => theme.backgroundSurface};
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d86624;
  pointer-events: none;
  transform: translate(-50%, -50%);
`
const HoverTip = styled.div<{ $left: number }>`
  position: absolute;
  top: 12px;
  left: ${({ $left }) => `${$left}%`};
  z-index: 1;
  padding: 6px 8px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  font-size: 12px;
  pointer-events: none;
  transform: translateX(-50%);
  white-space: nowrap;
`
const Bar = styled.button<{
  $height: number
  $tone: 'price' | 'volume' | 'liquidity' | 'depth'
}>`
  flex: 1;
  min-width: 0;
  height: ${({ $height }) => `${Math.max($height, 3)}%`};
  border-radius: 4px 4px 0 0;
  background: ${({ $tone }) =>
    $tone === 'volume' ? '#D86624' : $tone === 'depth' ? '#D86624' : $tone === 'liquidity' ? '#D86624' : '#D86624'};
  border: 0;
  padding: 0;
  cursor: crosshair;
  transition: opacity 100ms ease;

  &:hover,
  &:focus-visible {
    background: rgba(246, 160, 79, 0.55);
    outline: none;
  }
`
const TickBar = styled.button<{ $height: number; $side: 'above' | 'below' | 'active' }>`
  flex: 1;
  min-width: 5px;
  height: ${({ $height }) => `${Math.max($height, 3)}%`};
  padding: 0;
  background: ${({ $side }) => ($side === 'active' ? '#D86624' : $side === 'below' ? '#F6A04F' : '#a9b1b0')};
  border: 0;
  border-radius: 4px 4px 0 0;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    filter: brightness(0.82);
    outline: 2px solid ${({ theme }) => theme.accentAction};
    outline-offset: 2px;
  }
`
const TickTip = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 10px;
  font-size: 13px;
`
const PriceChange = styled.span<{ $positive: boolean }>`
  margin-left: 8px;
  color: ${({ $positive, theme }) => ($positive ? theme.accentSuccess : theme.accentFailure)};
  font-size: 14px;
`
const ChartFoot = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`
const DateLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
`
const ReverseButton = styled.button`
  margin-left: 8px;
  padding: 4px 8px;
  color: ${({ theme }) => theme.accentAction};
  background: ${({ theme }) => theme.accentActionSoft};
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
`
const Stats = styled.aside`
  display: grid;
  gap: 12px;
  align-content: start;
`
const Stat = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 14px;
`
const TransactionsSection = styled.section`
  margin-top: 24px;
`
const TransactionsTitle = styled.h2`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 21px;
`
const TransactionsCard = styled.div`
  overflow-x: auto;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`
const TransactionsTable = styled.table`
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;

  th,
  td {
    padding: 13px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
    color: ${({ theme }) => theme.textPrimary};
    font-size: 13px;
    text-align: left;
  }

  th {
    color: ${({ theme }) => theme.textSecondary};
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  tbody tr {
    transition: background 100ms ease;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.accentActionSoft};
  }
`
const TransactionLink = styled.a`
  color: ${({ theme }) => theme.accentAction};
  text-decoration: none;
`
const Label = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
`
const Value = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 21px;
  font-weight: 600;
`
const Notice = styled.div`
  padding: 18px;
  color: ${({ theme }) => theme.textSecondary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`

const fmt = (value: string | number | undefined, currency = false) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric === 0) return '-'
  if (currency && numeric < 0.01) return '<$0.01'
  return `${currency ? '$' : ''}${numeric.toLocaleString(undefined, {
    maximumSignificantDigits: 6,
  })}`
}
const label = (token: Token) => token.symbol || token.name || 'Token'
const shortAddress = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`
const formatChartDate = (timestamp: number, hourly = false) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(hourly ? { hour: 'numeric' } : {}),
  }).format(new Date(timestamp * 1000))

export default function AnalyticsPoolDetail() {
  const { protocol, poolId } = useParams<{
    protocol: 'v2' | 'v3'
    poolId: string
  }>()
  const v3 = protocol === 'v3'
  const [tab, setTab] = useState<'price' | 'volume' | 'liquidity' | 'depth'>('price')
  const [chartRange, setChartRange] = useState<ChartRange>('1w')
  const [inverted, setInverted] = useState(false)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [highlightedTickIndex, setHighlightedTickIndex] = useState<number | null>(null)
  const [swapStart] = useState(() => `${Math.floor(Date.now() / 1000) - 30 * 24 * 60 * 60}`)
  const {
    data: v3Data,
    loading: v3Loading,
    error: v3Error,
  } = useQuery<{ pool: V3Pool | null; bundles: { ethPriceUSD: string }[] }>(V3_POOL_QUERY, {
    client: analyticsApolloClient,
    variables: { id: poolId?.toLowerCase() ?? '', start: swapStart },
    skip: !v3,
  })
  const {
    data: v2Data,
    loading: v2Loading,
    error: v2Error,
  } = useQuery<{ pair: V2Pair | null; pairDayDatas: V2Day[] }>(V2_PAIR_QUERY, {
    client: v2AnalyticsApolloClient,
    variables: { id: poolId?.toLowerCase() ?? '' },
    skip: v3,
  })
  const pool = v3Data?.pool
  const pair = v2Data?.pair
  const loading = v3 ? v3Loading : v2Loading
  const error = v3 ? v3Error : v2Error
  const token0 = pool?.token0 ?? pair?.token0
  const token1 = pool?.token1 ?? pair?.token1
  const displayToken0 = inverted ? token1 : token0
  const displayToken1 = inverted ? token0 : token1
  const price = inverted ? pool?.token0Price ?? pair?.token0Price : pool?.token1Price ?? pair?.token1Price
  const liquidity = pool?.totalValueLockedUSD ?? pair?.reserveUSD
  const feeTier = pool ? `${Number(pool.feeTier) / 10_000}%` : 'V2'
  const allSnapshots: Snapshot[] = v3
    ? [...(pool?.poolDayData ?? [])].reverse().map((day) => ({
        id: day.id,
        date: day.date,
        volumeUSD: Number(day.volumeUSD),
        liquidityUSD: Number(day.tvlUSD),
        token0Price: Number(day.token0Price),
        token1Price: Number(day.token1Price),
      }))
    : [...(v2Data?.pairDayDatas ?? [])].reverse().map((day) => ({
        id: day.id,
        date: day.date,
        volumeUSD: Number(day.dailyVolumeUSD),
        liquidityUSD: Number(day.reserveUSD),
        token0Price: Number(day.reserve0) / Number(day.reserve1),
        token1Price: Number(day.reserve1) / Number(day.reserve0),
      }))
  const latestHour = Math.floor(Date.now() / 3_600_000) * 3_600
  const firstHour = latestHour - 23 * 3_600
  const hourlySnapshots: Snapshot[] = Array.from({ length: 24 }, (_, index) => ({
    id: `hour-${firstHour + index * 3_600}`,
    date: firstHour + index * 3_600,
    volumeUSD: 0,
    liquidityUSD: Number(liquidity),
    token0Price: Number(pool?.token0Price ?? pair?.token0Price),
    token1Price: Number(pool?.token1Price ?? pair?.token1Price),
  }))
  const indexedHours = v3 ? pool?.poolHourData ?? [] : pair?.pairHourData ?? []
  for (const hour of indexedHours) {
    const date = 'periodStartUnix' in hour ? hour.periodStartUnix : hour.hourStartUnix
    const bucket = Math.floor((date - firstHour) / 3_600)
    if (bucket < 0 || bucket >= hourlySnapshots.length) continue
    hourlySnapshots[bucket] =
      'periodStartUnix' in hour
        ? {
            id: hour.id,
            date,
            volumeUSD: Number(hour.volumeUSD),
            liquidityUSD: Number(hour.tvlUSD),
            token0Price: Number(hour.token0Price),
            token1Price: Number(hour.token1Price),
          }
        : {
            id: hour.id,
            date,
            volumeUSD: Number(hour.hourlyVolumeUSD),
            liquidityUSD: Number(hour.reserveUSD),
            token0Price: Number(hour.reserve0) / Number(hour.reserve1),
            token1Price: Number(hour.reserve1) / Number(hour.reserve0),
          }
  }
  const useHourlySnapshots =
    chartRange === '1d' && (tab === 'price' || tab === 'volume' || (!v3 && tab === 'liquidity'))
  const snapshots = useHourlySnapshots
    ? hourlySnapshots
    : chartRange === 'all'
    ? allSnapshots
    : allSnapshots.slice(-CHART_RANGE_DAYS[chartRange])
  const wonePriceUSD = Number(v3Data?.bundles[0]?.ethPriceUSD)
  const swapVolumeByDay = (pool?.swaps ?? []).reduce<Map<number, number>>((byDay, swap) => {
    const value = getV3SwapUsdValue(swap, wonePriceUSD, pool?.token0, pool?.token1)
    const day = Math.floor(Number(swap.timestamp) / 86_400) * 86_400
    byDay.set(day, (byDay.get(day) ?? 0) + (Number.isFinite(value) ? value : 0))
    return byDay
  }, new Map())
  const chartValues = snapshots.map((item) =>
    tab === 'volume'
      ? (() => {
          const indexedValue = item.volumeUSD
          const swapValue = swapVolumeByDay.get(item.date) ?? 0
          return v3 && !useHourlySnapshots && swapValue > 0 ? swapValue : indexedValue
        })()
      : tab === 'liquidity'
      ? item.liquidityUSD
      : tab === 'price'
      ? inverted
        ? item.token0Price
        : item.token1Price
      : 0
  )
  const depthValues = (pool?.ticks ?? []).map((tick) => Number(tick.liquidityGross))
  const showingTickLiquidity = v3 && (tab === 'liquidity' || tab === 'depth')
  const values = showingTickLiquidity ? depthValues : chartValues
  const maximum = Math.max(...values, 0)
  const tone = tab as 'price' | 'volume' | 'liquidity' | 'depth'
  const tabs: Array<'price' | 'volume' | 'liquidity' | 'depth'> = v3
    ? ['price', 'volume', 'liquidity', 'depth']
    : ['price', 'volume', 'liquidity']
  const linePoints = values
    .map(
      (value, index) =>
        `${values.length === 1 ? 50 : (index / (values.length - 1)) * 100},${
          100 - (maximum > 0 ? (value / maximum) * 92 + 4 : 50)
        }`
    )
    .join(' ')
  const areaPoints = `${values.length === 1 ? 50 : 0},100 ${linePoints} ${values.length === 1 ? 50 : 100},100`
  const minimumPrice = Math.min(...values.filter(Number.isFinite))
  const maximumPrice = Math.max(...values.filter(Number.isFinite))
  const priceRange = maximumPrice - minimumPrice || Math.max(maximumPrice * 0.02, 1)
  const priceAxisValues = Array.from(
    { length: 6 },
    (_, index) => maximumPrice + priceRange * 0.08 - ((priceRange * 1.16) / 5) * index
  )
  const hoverValue = hoverIndex === null ? undefined : values[hoverIndex]
  const hoverDate = hoverIndex === null ? undefined : snapshots[hoverIndex]?.date
  const readoutIndex = hoverIndex ?? snapshots.length - 1
  const readoutValue = values[readoutIndex]
  const readoutDate = snapshots[readoutIndex]?.date
  const showCornerReadout = tab === 'volume' || (!v3 && tab === 'liquidity')
  const periodLabel = CHART_RANGE_LABELS[chartRange]
  const dailyMonthlyVolume = snapshots.reduce((total, item) => total + item.volumeUSD, 0)
  const periodSwapVolume = snapshots.reduce((total, item) => total + (swapVolumeByDay.get(item.date) ?? 0), 0)
  const monthlyVolume = v3 && dailyMonthlyVolume <= 0 ? periodSwapVolume : dailyMonthlyVolume
  const monthlyFees = v3 ? monthlyVolume * (Number(pool?.feeTier) / 1_000_000) : undefined
  const firstPrice = chartValues.find((value) => Number.isFinite(value) && value > 0)
  const latestPrice = [...chartValues].reverse().find((value) => Number.isFinite(value) && value > 0)
  const monthlyPriceChange = firstPrice && latestPrice ? ((latestPrice - firstPrice) / firstPrice) * 100 : undefined
  const highlightedTick = highlightedTickIndex === null ? undefined : pool?.ticks[highlightedTickIndex]
  const highlightedTickPrice = highlightedTick
    ? Math.exp(Number(highlightedTick.tickIdx) * Math.log(1.0001)) *
      Math.pow(10, Number(token0?.decimals ?? 18) - Number(token1?.decimals ?? 18))
    : undefined
  const sortedTicks = pool?.ticks ?? []
  const activeTick = Number(pool?.tick)
  const bidTicks = sortedTicks.filter((tick) => Number(tick.tickIdx) <= activeTick)
  const askTicks = sortedTicks.filter((tick) => Number(tick.tickIdx) >= activeTick)
  const maxDepth = Math.max(
    bidTicks.reduce((total, tick) => total + Number(tick.liquidityGross), 0),
    askTicks.reduce((total, tick) => total + Number(tick.liquidityGross), 0),
    1
  )
  const buildDepthPoints = (ticks: typeof sortedTicks, start: number, end: number, reverse = false) => {
    const orderedTicks = reverse ? [...ticks].reverse() : ticks
    let cumulative = 0
    return orderedTicks
      .map((tick, index) => {
        cumulative += Number(tick.liquidityGross)
        const progress = orderedTicks.length <= 1 ? 1 : index / (orderedTicks.length - 1)
        return `${start + (end - start) * progress},${96 - (cumulative / maxDepth) * 82}`
      })
      .join(' ')
  }
  const bidDepthPoints = buildDepthPoints(bidTicks, 50, 0, true)
  const askDepthPoints = buildDepthPoints(askTicks, 50, 100)
  const dateLabelIndexes = Array.from(
    new Set([
      0,
      Math.floor((snapshots.length - 1) / 4),
      Math.floor((snapshots.length - 1) / 2),
      Math.floor(((snapshots.length - 1) * 3) / 4),
      snapshots.length - 1,
    ])
  ).filter((index) => index >= 0)

  if (!poolId || (protocol !== 'v2' && protocol !== 'v3'))
    return (
      <Page>
        <Notice>Choose a valid V2 or V3 pool from Analytics.</Notice>
      </Page>
    )
  if (loading)
    return (
      <Page>
        <Notice>Loading {v3 ? 'V3 pool' : 'V2 pair'} analytics…</Notice>
      </Page>
    )
  if (error || !token0 || !token1)
    return (
      <Page>
        <Back to="/analytics">← Analytics</Back>
        <Notice>Unable to load this pool. {error?.message ?? 'The pool is not indexed.'}</Notice>
      </Page>
    )

  return (
    <Page>
      <Back to="/analytics">← Analytics</Back>
      <Header>
        <TitleRow>
          <TokenIcons aria-hidden="true">
            <TokenIcon
              address={displayToken0.id}
              symbol={label(displayToken0)}
              chainId={SupportedChainId.HARMONY}
              size="38px"
              hideL2Icon
            />
            <TokenIcon
              $offset
              address={displayToken1.id}
              symbol={label(displayToken1)}
              chainId={SupportedChainId.HARMONY}
              size="38px"
              hideL2Icon
            />
          </TokenIcons>
          <Title>
            {label(displayToken0)} / {label(displayToken1)}
          </Title>
        </TitleRow>
        <Subtle>
          {v3 ? 'V3 pool' : 'V2 pair'} · {feeTier} · {poolId}
        </Subtle>
      </Header>
      <Tabs role="tablist" aria-label="Pool analytics chart">
        {tabs.map((item) => (
          <Tab key={item} type="button" role="tab" $active={tab === item} onClick={() => setTab(item)}>
            {item[0].toUpperCase() + item.slice(1)}
          </Tab>
        ))}
        {tab === 'price' && (
          <ReverseButton type="button" onClick={() => setInverted((current) => !current)}>
            ⇄ {label(inverted ? token0 : token1)}
          </ReverseButton>
        )}
      </Tabs>
      <Layout>
        <Card>
          <ChartTop>
            <div>
              <ChartTitle>{tab[0].toUpperCase() + tab.slice(1)}</ChartTitle>
              <ChartValue>
                {tab === 'price'
                  ? `1 ${label(inverted ? token1 : token0)} = ${fmt(price)} ${label(inverted ? token0 : token1)}`
                  : tab === 'volume'
                  ? fmt(monthlyVolume, true)
                  : tab === 'liquidity'
                  ? fmt(liquidity, true)
                  : 'Pool depth'}
              </ChartValue>
            </div>
            {showCornerReadout && readoutDate && readoutValue !== undefined && (
              <CornerReadout>
                {formatChartDate(readoutDate, useHourlySnapshots)}: <strong>{fmt(readoutValue, true)}</strong>
              </CornerReadout>
            )}
          </ChartTop>
          {tab === 'price' && monthlyPriceChange !== undefined && (
            <PriceChange $positive={monthlyPriceChange >= 0}>
              {monthlyPriceChange >= 0 ? '▲' : '▼'} {Math.abs(monthlyPriceChange).toFixed(2)}% past month
            </PriceChange>
          )}
          <ChartCaption>
            {showingTickLiquidity
              ? 'Liquidity distribution across indexed ticks. Select a bar for its tick price.'
              : useHourlySnapshots
              ? 'Showing 1D hourly snapshots.'
              : `Showing ${chartRange === 'all' ? 'all indexed' : chartRange.toUpperCase()} daily snapshots.`}
          </ChartCaption>
          {tab === 'price' ? (
            <PriceChart>
              <PriceChartCanvas
                onMouseLeave={() => setHoverIndex(null)}
                onMouseMove={(event) => {
                  const bounds = event.currentTarget.getBoundingClientRect()
                  const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
                  setHoverIndex(Math.round(ratio * Math.max(values.length - 1, 0)))
                }}
              >
                <LineChart viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Price history">
                  <defs>
                    <linearGradient id="pool-price-area" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F6A04F" stopOpacity="0.99" />
                      <stop offset="100%" stopColor="#F6A04F" stopOpacity="0.01" />
                    </linearGradient>
                  </defs>
                  <polygon points={areaPoints} fill="url(#pool-price-area)" />
                  <polyline points={linePoints} vectorEffect="non-scaling-stroke" />
                </LineChart>
                {hoverIndex !== null && hoverValue !== undefined && (
                  <>
                    <HoverLine $left={(hoverIndex / Math.max(values.length - 1, 1)) * 100} />
                    <HoverHorizontal $top={100 - (maximum > 0 ? (hoverValue / maximum) * 92 + 4 : 50)} />
                    <HoverPoint
                      $left={(hoverIndex / Math.max(values.length - 1, 1)) * 100}
                      $top={100 - (maximum > 0 ? (hoverValue / maximum) * 92 + 4 : 50)}
                    />
                    <HoverTip $left={(hoverIndex / Math.max(values.length - 1, 1)) * 100}>
                      {hoverDate ? formatChartDate(hoverDate, useHourlySnapshots) : 'Current'}:{' '}
                      <strong>
                        {fmt(hoverValue)} {label(inverted ? token0 : token1)}
                      </strong>
                    </HoverTip>
                  </>
                )}
              </PriceChartCanvas>
              <PriceAxis>
                {priceAxisValues.map((axisValue, index) => (
                  <span key={index}>{fmt(axisValue)}</span>
                ))}
              </PriceAxis>
            </PriceChart>
          ) : tab === 'depth' ? (
            <DepthChart
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label="Pool depth"
              onMouseMove={(event) => {
                const bounds = event.currentTarget.getBoundingClientRect()
                const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
                setHighlightedTickIndex(
                  Math.min(sortedTicks.length - 1, Math.max(0, Math.round(ratio * (sortedTicks.length - 1))))
                )
              }}
            >
              <polygon className="depth-bid" points={`0,96 ${bidDepthPoints} 50,96`} />
              <polygon className="depth-ask" points={`50,96 ${askDepthPoints} 100,96`} />
              <line className="depth-mid" x1="50" x2="50" y1="0" y2="96" />
            </DepthChart>
          ) : (
            <Chart
              $count={showingTickLiquidity ? pool?.ticks.length ?? 0 : values.length}
              onMouseLeave={() => setHoverIndex(null)}
            >
              {showingTickLiquidity ? (
                pool?.ticks.map((tick, index) => {
                  const tickValue = Number(tick.liquidityGross)
                  const side =
                    Number(tick.tickIdx) === Number(pool.tick)
                      ? 'active'
                      : Number(tick.tickIdx) < Number(pool.tick)
                      ? 'below'
                      : 'above'
                  return (
                    <TickBar
                      key={tick.id}
                      type="button"
                      $side={side}
                      $height={maximum > 0 ? (tickValue / maximum) * 100 : 0}
                      onMouseEnter={() => setHighlightedTickIndex(index)}
                      onFocus={() => setHighlightedTickIndex(index)}
                      onClick={() => setHighlightedTickIndex(index)}
                      aria-label={`Tick ${tick.tickIdx}`}
                    />
                  )
                })
              ) : values.length ? (
                values.map((value, index) => (
                  <Bar
                    key={index}
                    type="button"
                    $tone={tone}
                    $height={maximum > 0 ? (value / maximum) * 100 : 0}
                    title={fmt(value, true)}
                    aria-label={`${formatChartDate(snapshots[index].date, useHourlySnapshots)}: ${fmt(value, true)}`}
                    onMouseEnter={() => setHoverIndex(index)}
                    onFocus={() => setHoverIndex(index)}
                  />
                ))
              ) : (
                <Notice>No historical data has been indexed for this view yet.</Notice>
              )}
            </Chart>
          )}
          {showingTickLiquidity && highlightedTick && (
            <TickTip>
              Tick {highlightedTick.tickIdx} · 1 {label(token0)} = {fmt(highlightedTickPrice)} {label(token1)} ·
              liquidity {fmt(highlightedTick.liquidityGross)}
            </TickTip>
          )}
          {tab === 'price' ? (
            <DateLabels>
              {dateLabelIndexes.map((index) => (
                <span key={index}>
                  {snapshots[index] ? formatChartDate(snapshots[index].date, useHourlySnapshots) : 'Current'}
                </span>
              ))}
            </DateLabels>
          ) : (
            <ChartFoot>
              <span>{snapshots[0] ? formatChartDate(snapshots[0].date, useHourlySnapshots) : 'Current'}</span>
              <span>
                {snapshots[snapshots.length - 1]
                  ? formatChartDate(snapshots[snapshots.length - 1].date, useHourlySnapshots)
                  : 'Current'}
              </span>
            </ChartFoot>
          )}
          {(!v3 || (tab !== 'liquidity' && tab !== 'depth')) && (
            <RangeControls role="tablist" aria-label="Chart time range">
              {(['1d', '1w', '1y', 'all'] as const).map((range) => (
                <RangeButton
                  key={range}
                  type="button"
                  role="tab"
                  aria-selected={chartRange === range}
                  $active={chartRange === range}
                  onClick={() => setChartRange(range)}
                >
                  {range === 'all' ? 'All' : range.toUpperCase()}
                </RangeButton>
              ))}
            </RangeControls>
          )}
        </Card>
        <Stats>
          <Stat>
            <Label>Pool liquidity</Label>
            <Value>{fmt(liquidity, true)}</Value>
          </Stat>
          <Stat>
            <Label>{periodLabel} volume</Label>
            <Value>{fmt(monthlyVolume, true)}</Value>
          </Stat>
          <Stat>
            <Label>Current price</Label>
            <Value>
              1 {label(displayToken0)} = {fmt(price)} {label(displayToken1)}
            </Value>
          </Stat>
          <Stat>
            <Label>Pool balances</Label>
            <Value>
              {pool
                ? `${formatTokenQuantity(inverted ? pool.totalValueLockedToken1 : pool.totalValueLockedToken0)} ${label(
                    displayToken0
                  )}`
                : `${formatTokenQuantity(inverted ? pair?.reserve1 ?? 0 : pair?.reserve0 ?? 0)} ${label(
                    displayToken0
                  )}`}
            </Value>
            <Subtle>
              {pool
                ? `${formatTokenQuantity(inverted ? pool.totalValueLockedToken0 : pool.totalValueLockedToken1)} ${label(
                    displayToken1
                  )}`
                : `${formatTokenQuantity(inverted ? pair?.reserve0 ?? 0 : pair?.reserve1 ?? 0)} ${label(
                    displayToken1
                  )}`}
            </Subtle>
          </Stat>
          <Stat>
            <Label>{v3 ? `${periodLabel} fees` : 'Transactions'}</Label>
            <Value>{v3 ? fmt(monthlyFees, true) : fmt(pair?.txCount)}</Value>
          </Stat>
        </Stats>
      </Layout>
      <TransactionsSection>
        <TransactionsTitle>Recent transactions</TransactionsTitle>
        <TransactionsCard>
          <TransactionsTable>
            <thead>
              <tr>
                <th>Swap</th>
                <th>Value</th>
                <th>Account</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {(v3 ? pool?.swaps.slice(0, 20) : pair?.swaps)?.map((swap) => {
                const token0Input = 'amount0' in swap ? Number(swap.amount0) > 0 : Number(swap.amount0In) > 0
                const inputAmount = Math.abs(
                  Number(
                    'amount0' in swap
                      ? token0Input
                        ? swap.amount0
                        : swap.amount1
                      : token0Input
                      ? swap.amount0In
                      : swap.amount1In
                  )
                )
                const outputAmount = Math.abs(
                  Number(
                    'amount0' in swap
                      ? token0Input
                        ? swap.amount1
                        : swap.amount0
                      : token0Input
                      ? swap.amount1Out
                      : swap.amount0Out
                  )
                )
                const account = 'origin' in swap ? swap.origin : swap.from
                const swapValueUSD =
                  'amount0' in swap
                    ? getV3SwapUsdValue(swap, wonePriceUSD, pool?.token0, pool?.token1)
                    : Math.abs(Number(swap.amountUSD))
                return (
                  <tr key={swap.id}>
                    <td>
                      <TransactionLink
                        href={`https://explorer.harmony.one/tx/${swap.transaction.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {formatTokenQuantity(inputAmount)} {label(token0Input ? token0 : token1)} →{' '}
                        {formatTokenQuantity(outputAmount)} {label(token0Input ? token1 : token0)}
                      </TransactionLink>
                    </td>
                    <td>{fmt(swapValueUSD, true)}</td>
                    <td>{shortAddress(account)}</td>
                    <td>{new Date(Number(swap.timestamp) * 1000).toLocaleString()}</td>
                  </tr>
                )
              })}
              {!(v3 ? pool?.swaps.length : pair?.swaps.length) && (
                <tr>
                  <td colSpan={4}>No recent swaps indexed.</td>
                </tr>
              )}
            </tbody>
          </TransactionsTable>
        </TransactionsCard>
      </TransactionsSection>
    </Page>
  )
}
